import sample from 'lodash.sample'
import clone from 'lodash.clonedeep'
import questions from '@/content/questions.json'
import statements from '@/content/statements.json'
import challenges from '@/content/challenges.json'
import manifests from '@/content/manifests.json'
import * as scripts from '@/scripts'

const SEQUENCES = [
  ['question', 'statement', 'statement', 'question', 'challenge'],
  ['statement', 'question', 'statement', 'question', 'challenge'],
  ['question', 'statement', 'question', 'challenge']
]

/**
 * Send a Google Analytics event
 * @param {Object} data
 */
export const trackData = data => {
  window.dataLayer.push(data)
}

/**
 * Send a Formatted Google Analytics event
 * @param {String} action
 * @param {String} label
 */
export const trackEvent = (action, label) => {
  window.dataLayer.push({
    event: 'trackEvent',
    trackEventCategory: 'heldengame',
    trackEventAction: action,
    trackEventLabel: label
  })
}

/**
 * Request manifest by ID.
 * @param {Number} id
 * @returns {Object|undefined}
 */
const getManifest = id => manifests.find(manifest => manifest.id === id)

/**
 * Returns a random 'juich' instruction.
 * @returns {String|null}
 */
const getRandomInstruction = () => {
  return (Math.random() < 1 / 3)
    ? sample(['juich.am1', 'juich.am2', 'juich.gt1', 'juich.gt2'])
    : null
}

/**
 * Generate a tutorial script (data and scene mutations).
 * @returns {Object}
 */
export const generateTutorial = () => ({
  data: null,
  mutations: scripts.TUTORIAL
})

/**
 * Generate a question script (data and scene mutations).
 * @param {Number} level Group level
 * @param {Array} history Questions that have already been used
 * @returns {Object}
 */
export const generateQuestion = (level, history) => {
  let collection = questions
    .filter(question => question.level === 5678 || question.level === level)
    .filter(question => !history.includes(question.id))

  if (!collection.length) collection = questions

  const data = { ...sample(collection) }

  const script = history.length > 0
    ? scripts.QUESTION
    : scripts.FIRST_QUESTION

  const mutations = clone(script).map(mutation => {
    if (mutation.isCompleted && mutation.instruction === null) mutation.instruction = getRandomInstruction()
    return mutation
  })

  return { data, mutations }
}

/**
 * Generate a statement script (data and scene mutations).
 * @param {Number} level Group level
 * @param {Array} history Statements that have already been used
 * @returns {Object}
 */
export const generateStatement = (level, history) => {
  let collection = statements
    .filter(statement => statement.level === 5678 || statement.level === level)
    .filter(statement => !history.includes(statement.id))

  if (!collection.length) collection = statements

  const data = { ...sample(collection) }

  data.manifests = data.answers.map(answer => getManifest(answer.manifest))
  data.answers = data.answers.map(answer => answer.label)

  const script = history.length > 0
    ? scripts.STATEMENT
    : scripts.FIRST_STATEMENT

  const mutations = clone(script).map(mutation => {
    if (mutation.isCompleted && mutation.instruction === null) mutation.instruction = getRandomInstruction()
    return mutation
  })

  return { data, mutations }
}

/**
 * Generate a challenge script (data and scene mutations).
 * @param {Number} level Group level
 * @param {Array} badges Badges that have been collected
 * @param {Array} history Challenges that have already been used
 * @returns {Object}
 */
export const generateChallenge = (level, currentData, badges, history) => {
  let collection = challenges
    .filter(challenge => challenge.level === 5678 || challenge.level === level)
    .filter(challenge => !badges.includes(challenge.badge))
    .filter(challenge => !history.includes(challenge.id))

  if (!collection.length) collection = challenges

  const data = { ...sample(collection) }

  const script = history.length > 0
    ? scripts.CHALLENGE
    : scripts.FIRST_CHALLENGE

  const sections = data.grouping === 'groep'
    ? currentData.answers.length
    : 1

  const mutations = clone(script).map(mutation => {
    if ('sections' in mutation && mutation.sections === null) mutation.sections = sections
    if ('timeout' in mutation && mutation.timeout === null) mutation.timeout = data.timeout
    if (mutation.isCompleted) mutation.instruction = `pp.${data.badge}.gehaald`
    return mutation
  })

  return { data, mutations }
}

/**
 * Generate a new script (data and scene mutations).
 * @param {Object} state Current game state
 * @returns {Object}
 */
export const generateScript = state => {
  let round = 0
  let step = 0

  state.history.forEach(item => {
    if (item.type === 'challenge') {
      round += 1
      step = 0
    } else {
      step += 1
    }
  })

  const index = Math.min(round, 2)
  const sequence = SEQUENCES[index]
  const type = sequence[step]

  const history = state.history
    .filter(item => item.type === type)
    .map(item => item.id)

  switch (type) {
    case 'question':
      return generateQuestion(state.level, history)
    case 'statement':
      return generateStatement(state.level, history)
    case 'challenge':
      return generateChallenge(state.level, state.data, state.badges, history)
    default:
      console.error(`Could not generate script for ${type}`)
      return { data: null, mutations: [] }
  }
}
